import React, { memo } from 'react';

import { Box } from '@mui/material';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { SKELETON_TYPES } from 'constants/skeleton';

import Card from './Card';
import Checkbox from './Checkbox';
import List from './ListItem';
import Paper from './Paper';
import { SkeletonTestIds } from './Skeleton.testIds';

const getStyles = ({ type, height, width }) => ({
  item: {
    display: 'flex',
    alignItems: 'center',
    minHeight: height ? `${height}px` : 'auto',
    width: width ? `${width}px` : undefined,
    padding: type === SKELETON_TYPES.PAPER_SMALL ? '10px 27px' : undefined,

    '&:not(:last-child)': {
      borderBottom: `${
        [SKELETON_TYPES.LIST, SKELETON_TYPES.TABLE].includes(type) ? 1 : 0
      }px solid rgba(128,128,128,0.5)`,
    },
  },
});

const Skeleton = ({
  length = 20,
  cellsNumber,
  type = SKELETON_TYPES.LIST,
  height = 64,
  isEmptyLine = false,
  width,
  customContent,
  className,
}) => {
  const classes = getStyles({ type, height, width });

  const renderContent = () => {
    switch (type) {
      case SKELETON_TYPES.CARD:
        return <Card />;

      case SKELETON_TYPES.LIST:
      case SKELETON_TYPES.TEXT_INPUT:
      case SKELETON_TYPES.TABLE:
        return <List cellsNumber={cellsNumber} type={type} />;

      case SKELETON_TYPES.PAPER_SMALL:
      case SKELETON_TYPES.PAPER_HUGE:
        return <Paper type={type} />;

      case SKELETON_TYPES.CHECKBOX:
        return <Checkbox data-testid={SkeletonTestIds.CHECKBOX} />;

      default:
        break;
    }
  };

  return (
    <div className={clsx(className, 'w-full flex flex-col')}>
      {isEmptyLine && <Box sx={classes.item} />}

      {Array.from({ length }).map((_, index) => (
        <Box key={index} sx={classes.item}>
          {customContent ?? renderContent()}
        </Box>
      ))}
    </div>
  );
};

Skeleton.propTypes = {
  isEmptyLine: PropTypes.bool,
  length: PropTypes.number,
  type: PropTypes.string,
  height: PropTypes.number,
  width: PropTypes.number,
  cellsNumber: PropTypes.number,
  customContent: PropTypes.element,
  className: PropTypes.string,
};

export default memo(Skeleton);
